import {
    ClassificationResult,
    DeduplicationResult,
    ProcessingStatus,
    ResponsibleResult,
    SensitiveResult,
    TaskStatus
} from '@idea2app/ai-work-order-service';
import { Icon, Loading } from 'idea-react';
import { computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { Filter } from 'mobx-restful';
import { Column, Field, RestTable } from 'mobx-restful-table';
import { Component } from 'react';
import {
    Badge,
    Button,
    Card,
    Col,
    Container,
    FormControl,
    InputGroup,
    Row
} from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-class-tools';

import { LogEntry, TaskLogs } from '../component/TaskLogs';
import { TaskStep, TaskSteps } from '../component/TaskSteps';
import classificationTaskStore from '../model/ClassificationTask';
import deduplicationTaskStore from '../model/DeduplicationTask';
import { TaskResultModel, TaskType } from '../model/TaskResult';
import { i18n, I18nKey, t } from '../model/Translation';

const ProcessingStatusColors: Record<ProcessingStatus, string> = {
    pending: 'warning',
    processed: 'success',
    no_change: 'secondary',
    manual_review: 'info'
};

interface OptimizationTaskDetailParams {
    type: TaskType;
    id: string;
}

@withRouter
@observer
export class OptimizationTaskDetailPage extends Component<
    RouteComponentProps<OptimizationTaskDetailParams>
> {
    @observable
    accessor logs: LogEntry[] = [];

    @computed
    get filterFields(): Field<Filter<DeduplicationResult>>[] {
        return [
            {
                key: 'processingStatus',
                type: 'select',
                renderLabel: t('status'),
                options: [
                    { value: '', label: t('all_statuses') },
                    { value: 'pending', label: t('pending') },
                    { value: 'processed', label: t('processed') },
                    { value: 'no_change', label: t('no_change') },
                    { value: 'manual_review', label: t('manual_review') }
                ]
            }
        ];
    }

    get taskStore() {
        const { type } = this.props.match.params;

        return type === 'classification'
            ? classificationTaskStore
            : deduplicationTaskStore;
    }

    get taskResultStore() {
        const { type, id } = this.props.match.params;

        return new TaskResultModel(type, +id);
    }

    componentDidMount() {
        const { id } = this.props.match.params;

        deduplicationTaskStore.getOne(+id);

        this.startLogPolling();
    }

    componentWillUnmount() {
        this.stopLogPolling();
    }

    private logPollingTimer?: NodeJS.Timeout;

    startLogPolling() {
        this.logPollingTimer = setInterval(this.addMockLog, 2000);
    }

    stopLogPolling() {
        if (this.logPollingTimer) clearInterval(this.logPollingTimer);
    }

    addMockLog = () => {
        const now = new Date();
        const timestamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;

        const messages = [
            {
                level: 'SUCCESS' as const,
                message: i18n.t('duplicate_workorder_found', { count: 3 })
            },
            {
                level: 'INFO' as const,
                message: i18n.t('analyzing_workorders')
            },
            {
                level: 'WARNING' as const,
                message: i18n.t('manual_review')
            }
        ];

        const randomMessage =
            messages[Math.floor(Math.random() * messages.length)];

        this.logs = [
            ...this.logs,
            {
                timestamp,
                level: randomMessage.level,
                message: randomMessage.message
            }
        ];
        // Keep only last 50 logs
        if (this.logs.length > 50) this.logs = this.logs.slice(-50);
    };

    @computed
    get taskSteps(): TaskStep[] {
        const { status, processedWorkOrders, totalWorkOrders } =
            deduplicationTaskStore.currentOne;

        const isInProgress = status === ('in_progress' as TaskStatus);
        const progress =
            ((processedWorkOrders || 0) / (totalWorkOrders || 1)) * 100;

        return [
            {
                title: t('data_preprocessing'),
                summary: t('reading_classification_workorders', {
                    task: totalWorkOrders || 0
                })
            },
            {
                title: t('duplicate_workorder_analysis'),
                summary: isInProgress
                    ? t('analysis_progress', {
                          percent: progress.toFixed(0),
                          count: this.taskResultStore.totalCount
                      })
                    : undefined
            },
            {
                title: t('completed')
            }
        ];
    }

    @computed
    get commonColumns(): {
        id: Column<ClassificationResult | DeduplicationResult>;
        workOrder: Column<ClassificationResult | DeduplicationResult>;
        processingStatus: Column<ClassificationResult | DeduplicationResult>;
        createdAt: Column<ClassificationResult | DeduplicationResult>;
    } {
        return {
            id: {
                key: 'id',
                type: 'number',
                renderHead: t('id_number')
            },
            workOrder: {
                key: 'workOrder',
                renderHead: t('work_order_id'),
                renderBody: ({ workOrder }) => workOrder?.orderNumber || '-'
            },
            processingStatus: {
                key: 'processingStatus',
                renderHead: t('status'),
                renderBody: ({ processingStatus }) => (
                    <Badge bg={ProcessingStatusColors[processingStatus]}>
                        {t(processingStatus as I18nKey)}
                    </Badge>
                )
            },
            createdAt: {
                key: 'createdAt',
                renderHead: t('processing_time'),
                renderBody: ({ createdAt }) =>
                    new Date(createdAt).toLocaleString()
            }
        };
    }

    @computed
    get classificationColumns(): Column<ClassificationResult>[] {
        const { id, workOrder, processingStatus, createdAt } =
            this.commonColumns;
        return [
            id,
            workOrder,
            {
                key: 'oldCategory',
                renderHead: t('original_category'),
                renderBody: ({ oldCategory }) =>
                    oldCategory?.level1Category || '-'
            },
            {
                key: 'newCategory',
                renderHead: t('new_category_tag'),
                renderBody: ({ newCategory }) =>
                    newCategory?.level1Category || '-'
            },
            {
                key: 'changeReason',
                renderHead: t('change_reason'),
                renderBody: ({ changeReason }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {changeReason || '-'}
                    </div>
                )
            },
            processingStatus,
            createdAt
        ];
    }

    @computed
    get deduplicationColumns(): Column<DeduplicationResult>[] {
        const { id, workOrder, processingStatus, createdAt } =
            this.commonColumns;
        return [
            id,
            workOrder,
            {
                key: 'duplicateWorkOrder',
                renderHead: t('corresponding_workorder'),
                renderBody: ({ duplicateWorkOrder }) =>
                    duplicateWorkOrder?.orderNumber || '-'
            },
            {
                key: 'duplicateReason',
                renderHead: t('reason_for_duplication'),
                renderBody: ({ duplicateReason }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {duplicateReason || '-'}
                    </div>
                )
            },
            {
                key: 'similarityScore',
                renderHead: t('similarity'),
                renderBody: ({ similarityScore }) =>
                    similarityScore
                        ? `${(similarityScore * 100).toFixed(1)}%`
                        : '-'
            },
            processingStatus,
            createdAt
        ];
    }

    @computed
    get sensitiveColumns(): Column<SensitiveResult>[] {
        const { id, workOrder, processingStatus, createdAt } =
            this.commonColumns;
        return [
            id,
            workOrder,
            {
                key: 'sensitiveType',
                renderHead: t('sensitive_type')
            },
            {
                key: 'aiExtractionReason',
                renderHead: t('ai_extraction_reason'),
                renderBody: ({ aiExtractionReason }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {aiExtractionReason || '-'}
                    </div>
                )
            },
            processingStatus,
            createdAt
        ];
    }

    @computed
    get responsibleColumns(): Column<ResponsibleResult>[] {
        const { id, workOrder, processingStatus, createdAt } =
            this.commonColumns;
        return [
            id,
            workOrder,
            {
                key: 'unitName',
                renderHead: t('unit_name')
            },
            {
                key: 'powerSupplyOffice',
                renderHead: t('power_supply_office')
            },
            {
                key: 'areaManager',
                renderHead: t('area_manager')
            },
            {
                key: 'violationIssue',
                renderHead: t('violation_issue'),
                renderBody: ({ violationIssue }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {violationIssue || '-'}
                    </div>
                )
            },
            {
                key: 'rectificationRequirement',
                renderHead: t('rectification_requirement'),
                renderBody: ({ rectificationRequirement }) => (
                    <div
                        className="text-truncate"
                        style={{ maxWidth: '20rem' }}
                    >
                        {rectificationRequirement || '-'}
                    </div>
                )
            },
            {
                key: 'referencedPolicy',
                renderHead: t('referenced_policy'),
                renderBody: ({ referencedPolicy }) =>
                    referencedPolicy?.title || '-'
            },
            processingStatus,
            createdAt
        ];
    }

    render() {
        const { downloading, currentOne } = deduplicationTaskStore;

        return (
            <Container className="py-4" fluid>
                {downloading > 0 && <Loading />}

                <Row className="mb-4">
                    <Col md={6}>
                        <Card className="p-3 shadow-sm" body>
                            <TaskSteps
                                task={currentOne}
                                steps={this.taskSteps}
                                currentStep={1}
                            />
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="shadow-sm" body>
                            <Card.Title as="h2" className="fs-5">
                                <Icon name="terminal" className="me-2" />
                                {t('real_time_logs')}
                            </Card.Title>

                            <TaskLogs logs={this.logs} autoScroll />
                        </Card>
                    </Col>
                </Row>

                <Card className="shadow-sm" body>
                    <header className="d-flex justify-content-between align-items-center gap-3">
                        <Card.Title as="h2" className="fs-5 flex-fill">
                            {t('deduplication_result')} (
                            {this.taskResultStore.totalCount})
                        </Card.Title>
                        <InputGroup className="w-auto">
                            <InputGroup.Text>
                                <Icon name="search" />
                            </InputGroup.Text>
                            <FormControl
                                type="search"
                                placeholder={t('search')}
                            />
                        </InputGroup>
                        <Button>{t('export_file')}</Button>
                    </header>

                    <RestTable
                        className="text-center"
                        filterFields={this.filterFields}
                        columns={this.deduplicationColumns}
                        store={this.taskResultStore}
                        translator={i18n}
                        striped
                        hover
                    />
                </Card>
            </Container>
        );
    }
}
